import type { SearchResult } from '@hooks/useQuickStats';
import {
  getLeagueLabel,
  getLeagueLogoUrl,
  hasPlayerStatsOn,
  type LeagueKey,
} from '@parlay-picker/shared/league';
import { Search, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { ErrorBanner, KeyboardHints, LoadingOverlay, ModalShell } from './ModalSharedParts';
import { PlayerResultRow, TeamResultRow } from './SearchResultRows';

interface SearchViewProps {
  query: string;
  results: SearchResult[];
  selectedIndex: number;
  recentSearches: SearchResult[];
  selectedLeagues: Set<LeagueKey>;
  leagueOptions: LeagueKey[];
  isSearching: boolean;
  isLoading: boolean;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onQueryChange: (q: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSelect: (result: SearchResult) => void;
  onToggleLeague: (key: LeagueKey) => void;
  onClearRecent: () => void;
  onClose: () => void;
}

export function SearchView({
  query,
  results,
  selectedIndex,
  recentSearches,
  selectedLeagues,
  leagueOptions,
  isSearching,
  isLoading,
  error,
  inputRef,
  onQueryChange,
  onKeyDown,
  onSelect,
  onToggleLeague,
  onClearRecent,
  onClose,
}: SearchViewProps): ReactElement {
  const filteredRecentSearches = recentSearches.filter((r) => selectedLeagues.has(r.leagueKey));

  const displayResults = query.length >= 2 ? results : filteredRecentSearches;
  const showRecentLabel = query.length < 2 && filteredRecentSearches.length > 0;

  return (
    <ModalShell onClose={onClose}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-theme-border">
        <Search className="w-5 h-5 text-theme-text-secondary" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search player or team..."
          className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-lg"
          disabled={isLoading}
        />
        {isSearching && (
          <div className="w-5 h-5 border-2 border-gray-500 border-t-blue-500 rounded-full animate-spin" />
        )}
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-secondary hover:text-white transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* League filter pills (multi-select) */}
      <div className="flex gap-1.5 px-4 py-2 border-b border-theme-border/50 overflow-x-auto scrollbar-none">
        {leagueOptions.map((key) => {
          const active = selectedLeagues.has(key);
          const logoUrl = getLeagueLogoUrl(key);
          const offSeason = !hasPlayerStatsOn(key);
          return (
            <button
              type="button"
              key={key}
              onClick={() => !offSeason && onToggleLeague(key)}
              disabled={offSeason}
              className={`shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition ${
                active
                  ? 'bg-theme-primary/20 text-theme-primary ring-1 ring-theme-primary/40'
                  : 'bg-theme-surface-hover/50 text-theme-text-tertiary hover:text-white'
              } ${offSeason ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {logoUrl && <img src={logoUrl} alt="" className="w-3.5 h-3.5" />}
              {getLeagueLabel(key)}
            </button>
          );
        })}
      </div>

      {isLoading && <LoadingOverlay />}
      {error && <ErrorBanner error={error} />}

      {/* Results */}
      <div className="max-h-80 overflow-y-auto">
        {showRecentLabel && (
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-theme-text-tertiary uppercase tracking-wide">
              Recent Searches
            </span>
            <button
              type="button"
              onClick={onClearRecent}
              className="text-xs text-theme-text-tertiary hover:text-theme-text transition"
            >
              Clear
            </button>
          </div>
        )}

        {displayResults.length === 0 && query.length >= 2 && !isSearching && (
          <div className="p-4 text-center text-theme-text-tertiary">No players or teams found</div>
        )}

        {displayResults.length === 0 && query.length < 2 && recentSearches.length === 0 && (
          <div className="p-4 text-center text-theme-text-tertiary">
            Type at least 2 characters to search
          </div>
        )}

        {displayResults.map((result, index) => (
          <button
            type="button"
            key={result.type === 'player' ? `p-${result.id}` : `t-${result.name}`}
            onClick={() => onSelect(result)}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
              index === selectedIndex
                ? 'bg-theme-primary/30 border-l-2 border-theme-primary'
                : 'hover:bg-theme-surface-hover border-l-2 border-transparent'
            }`}
          >
            {result.type === 'player' ? (
              <PlayerResultRow player={result} leagueKey={result.leagueKey} />
            ) : (
              <TeamResultRow team={result} leagueKey={result.leagueKey} />
            )}
          </button>
        ))}
      </div>

      <KeyboardHints closeLabel="close" />
    </ModalShell>
  );
}
